
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { 
  generateSEOKeywords, 
  generateMetaDescription, 
  generatePageTitle, 
  generateH1Title 
} from '@/utils/seoKeywords';

interface SEOPageData {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogImage: string;
  ogType: 'website' | 'article';
  h1Title: string;
  geoData?: {
    latitude?: number;
    longitude?: number;
    region?: string;
    country?: string;
  };
  structuredData?: any[];
}

interface UseSEODataProps {
  pageType?: 'home' | 'city' | 'country' | 'therapist' | 'search' | 'static';
  entityData?: any;
  customData?: Partial<SEOPageData>;
}

export const useSEOData = ({ pageType = 'static', entityData, customData }: UseSEODataProps = {}) => {
  const location = useLocation();
  const { t, language } = useLanguage();
  const baseUrl = 'https://psychotherapist.help';

  const seoData = useMemo(() => {
    const currentPath = location.pathname;
    
    // Generate SEO data using the new keyword system
    const generatedTitle = generatePageTitle(pageType, entityData);
    const generatedDescription = generateMetaDescription(pageType, entityData);
    const generatedKeywords = generateSEOKeywords(pageType, entityData);
    const generatedH1 = generateH1Title(pageType, entityData);
    
    // Default SEO data with enhanced structure
    let seoData: SEOPageData = {
      title: generatedTitle,
      description: generatedDescription,
      keywords: generatedKeywords.join(', '),
      canonicalUrl: `${baseUrl}${currentPath}`,
      ogImage: 'https://psychotherapist.help/og-image.png',
      ogType: 'website' as const,
      h1Title: generatedH1,
      structuredData: []
    };

    // Enhanced structured data based on page type
    const generateStructuredData = () => {
      const commonStructuredData = [
        {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Global Therapy Connect",
          "url": baseUrl,
          "logo": `${baseUrl}/logo.png`,
          "description": "Global platform connecting patients with verified mental health professionals worldwide",
          "sameAs": [
            "https://www.facebook.com/globaltherapyconnect",
            "https://www.twitter.com/globaltherapy",
            "https://www.linkedin.com/company/global-therapy-connect"
          ]
        }
      ];

      switch (pageType) {
        case 'city':
          if (entityData) {
            const cityName = entityData.name ? entityData.name.replace(/\s+\d+$/, '').trim() : 'Unknown City';
            return [
              ...commonStructuredData,
              {
                "@context": "https://schema.org",
                "@type": "LocalBusiness",
                "name": `Mental Health Services in ${cityName}`,
                "description": `Professional mental health services in ${cityName}, ${entityData.country || 'Unknown Country'}`,
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": cityName,
                  "addressCountry": entityData.country || 'Unknown Country'
                },
                ...(entityData.latitude && entityData.longitude && {
                  "geo": {
                    "@type": "GeoCoordinates",
                    "latitude": entityData.latitude,
                    "longitude": entityData.longitude
                  }
                }),
                "serviceArea": {
                  "@type": "City",
                  "name": cityName
                }
              }
            ];
          }
          break;

        case 'country':
          if (entityData?.name) {
            return [
              ...commonStructuredData,
              {
                "@context": "https://schema.org",
                "@type": "Service",
                "name": `Mental Health Services in ${entityData.name}`,
                "description": `Professional mental health and therapy services throughout ${entityData.name}`,
                "serviceType": "Mental Health Services",
                "areaServed": {
                  "@type": "Country",
                  "name": entityData.name
                },
                "provider": {
                  "@type": "Organization",
                  "name": "Global Therapy Connect"
                }
              }
            ];
          }
          break;

        case 'home':
          return [
            ...commonStructuredData,
            {
              "@context": "https://schema.org",
              "@type": "WebSite",
              "name": "Global Therapy Connect",
              "url": baseUrl,
              "description": "Global platform for professional mental health support and therapy services",
              "potentialAction": {
                "@type": "SearchAction",
                "target": {
                  "@type": "EntryPoint",
                  "urlTemplate": `${baseUrl}/find?q={search_term_string}`
                },
                "query-input": "required name=search_term_string"
              }
            }
          ];
      }

      return commonStructuredData;
    };

    seoData.structuredData = generateStructuredData();

    // Customize based on page type with enhanced data
    switch (pageType) {
      case 'city':
        if (entityData) {
          seoData.ogImage = `https://psychotherapist.help/og-city-${entityData.slug || 'default'}.png`;
          seoData.geoData = {
            latitude: entityData.latitude,
            longitude: entityData.longitude,
            region: entityData.name,
            country: entityData.country
          };
        }
        break;

      case 'country':
        if (entityData) {
          seoData.ogImage = `https://psychotherapist.help/og-country-${entityData.slug || 'default'}.png`;
          seoData.geoData = {
            country: entityData.name
          };
        }
        break;

      case 'home':
        seoData.ogImage = 'https://psychotherapist.help/og-home.png';
        break;

      case 'search':
        seoData.ogImage = 'https://psychotherapist.help/og-search.png';
        break;
    }

    // Override with custom data if provided
    if (customData) {
      seoData = {
        ...seoData,
        ...customData
      };
    }

    return seoData;
  }, [pageType, entityData, customData, location.pathname]);

  return seoData;
};
